// src/components/PlayPreferences.tsx

import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonText,
  IonNote,
  IonIcon,
} from '@ionic/react';
import { globeOutline } from 'ionicons/icons';

export type PlayPreference = 'in-person' | 'online';
export type OnlinePlatform =
  | 'board-game-arena'
  | 'tabletop-simulator'
  | 'tabletopia'
  | 'roll20'
  | 'foundry-vtt'
  | 'discord'
  | 'other';

interface PlayPreferencesProps {
  currentPreferences: PlayPreference[];
  currentPlatforms: OnlinePlatform[];
  onPreferencesChange: (preferences: PlayPreference[]) => void;
  onPlatformsChange: (platforms: OnlinePlatform[]) => void;
}

export const PlayPreferences: React.FC<PlayPreferencesProps> = ({
  currentPreferences,
  currentPlatforms,
  onPreferencesChange,
  onPlatformsChange,
}) => {
  const preferenceOptions: { value: PlayPreference; label: string; description: string }[] = [
    {
      value: 'in-person',
      label: 'In-Person Play',
      description: 'Meet up locally to play games'
    },
    {
      value: 'online',
      label: 'Online Play',
      description: 'Play remotely via digital platforms'
    },
  ];

  const platformOptions: { value: OnlinePlatform; label: string }[] = [
    { value: 'board-game-arena', label: 'Board Game Arena' },
    { value: 'tabletop-simulator', label: 'Tabletop Simulator' },
    { value: 'tabletopia', label: 'Tabletopia' },
    { value: 'roll20', label: 'Roll20' },
    { value: 'foundry-vtt', label: 'Foundry VTT' },
    { value: 'discord', label: 'Discord' },
    { value: 'other', label: 'Other' },
  ];

  const handlePreferenceToggle = (preference: PlayPreference) => {
    if (currentPreferences.includes(preference)) {
      const updated = currentPreferences.filter(p => p !== preference);
      onPreferencesChange(updated);

      // If unchecking 'online', clear platforms
      if (preference === 'online') {
        onPlatformsChange([]);
      }
    } else {
      onPreferencesChange([...currentPreferences, preference]);
    }
  };

  const handlePlatformToggle = (platform: OnlinePlatform) => {
    if (currentPlatforms.includes(platform)) {
      onPlatformsChange(currentPlatforms.filter(p => p !== platform));
    } else {
      onPlatformsChange([...currentPlatforms, platform]);
    }
  };

  const hasOnlinePreference = currentPreferences.includes('online');

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>How You Play</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {/* Play preferences */}
        <div style={{ marginBottom: '24px' }}>
          <IonLabel>
            <strong>Play Preferences</strong>
          </IonLabel>
          <IonNote style={{ display: 'block', marginTop: '4px', marginBottom: '8px', fontSize: '12px' }}>
            Select all that apply
          </IonNote>
          {preferenceOptions.map(option => (
            <IonItem
              key={option.value}
              lines="none"
              button
              onClick={() => handlePreferenceToggle(option.value)}
            >
              <IonCheckbox
                slot="start"
                checked={currentPreferences.includes(option.value)}
                onIonChange={() => handlePreferenceToggle(option.value)}
              />
              <IonLabel>
                <h3>{option.label}</h3>
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                  {option.description}
                </p>
              </IonLabel>
            </IonItem>
          ))}
        </div>

        {/* Online platforms (only show if 'online' is selected) */}
        {hasOnlinePreference && (
          <>
            <div style={{ marginTop: '24px', marginBottom: '12px' }}>
              <IonLabel>
                <strong>Online Platforms *</strong>
              </IonLabel>
              <IonNote style={{ display: 'block', marginTop: '4px', marginBottom: '8px', fontSize: '12px' }}>
                Select at least one platform you use for online play
              </IonNote>
            </div>

            {platformOptions.map(option => (
              <IonItem
                key={option.value}
                lines="none"
                button
                onClick={() => handlePlatformToggle(option.value)}
              >
                <IonCheckbox
                  slot="start"
                  checked={currentPlatforms.includes(option.value)}
                  onIonChange={() => handlePlatformToggle(option.value)}
                />
                <IonLabel>{option.label}</IonLabel>
              </IonItem>
            ))}
          </>
        )}
      </IonCardContent>
    </IonCard>
  );
};